import { Injectable, inject } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { UserInterfaceClientConfig } from '../../../client-config/bma-user-interface-config';
import { EnvironmentConfig } from '../../../client-config/index';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { DeviceService } from '../../../core/services/device/device.service';
import { StorageService } from '../../../core/services/storage/storage.service';
import { UserService } from '../../../core/services/user/user.service';
import { WindowRefService } from '../../../core/services/windowRef/window-ref.service';
import { VanillaApiService } from '../../../vanillaInit/services/vanillaApi/vanilla-api.service';
import { ISegmentData } from '../model/segment-data.model';

@Injectable({ providedIn: 'root' })
export class CoralSportsSegmentProviderService {
    private readonly SEGMENT_STORE_KEYTEXT: string = 'Segment';
    private readonly OTF_SEGMENT_STORE_KEYTEXT: string = 'OTF_SEGMENT';
    private readonly NAME_IDENTIFIER_KEY: string = 'http://api.bwin.com/v3/user/pg/nameidentifier';

    public isOTFAvailable: BehaviorSubject<any> = new BehaviorSubject({});
    private readonly environment = inject(EnvironmentConfig).configuration;

    constructor(
        private vanillaApiService: VanillaApiService,
        private pubsubService: PubSubService,
        private storageService: StorageService,
        private userService: UserService,
        private deviceService: DeviceService,
        private userInterfaceConfig: UserInterfaceClientConfig,
        protected windowRef: WindowRefService,
    ) {}

    /**
     * get segment value and bind to localstorage object
     */
    getSegmentDetails(firstTimeLogin?: boolean) {
        const segmentExpiryTime = this.userInterfaceConfig.cspSegmentExpiry ? this.userInterfaceConfig.cspSegmentExpiry : 15;
        const currentTime = new Date();
        const currentUTCHours = currentTime.getUTCHours();
        let expiryHours;
        if (currentUTCHours < segmentExpiryTime) {
            expiryHours = segmentExpiryTime - currentUTCHours;
        } else {
            expiryHours = 24 - currentUTCHours + segmentExpiryTime;
        }
        currentTime.setHours(currentTime.getHours() + expiryHours);
        const expiryDateTime = currentTime.toUTCString();
        if (this.deviceService.requestPlatform === 'mobile') {
            this.getCoralSportsSegment().subscribe(
                (segmentData: ISegmentData[]) => {
                    const segmentDataValue =
                        segmentData &&
                        segmentData.find((segment) => {
                            return segment.group?.startsWith('CSP_');
                        });
                    const segmentValue = segmentDataValue === undefined ? '' : segmentDataValue.group;
                    this.storageService.set(this.SEGMENT_STORE_KEYTEXT, {
                        user: this.userService.username,
                        segment: segmentValue,
                        timestamp: expiryDateTime,
                    });
                    if (firstTimeLogin && segmentValue) {
                        this.pubsubService.publish(this.pubsubService.API.SEGMENT_RECEIVED, { action: 'GET_SEGMENT_API=>SUCCESS=>' + segmentValue });
                    }
                },
                (err) => {
                    console.warn(err);
                    this.storageService.set(this.SEGMENT_STORE_KEYTEXT, { user: this.userService.username, segment: '', timestamp: expiryDateTime });
                    this.pubsubService.publish(this.pubsubService.API.SEGMENT_API_FAILED, { action: 'GET_SEGMENT_API=>FAILED' });
                },
            );
        }
    }

    /**
     * calls coralSports API to return segment mapped to user
     * @returns Observable<ISegmentData[]>
     */
    public getCoralSportsSegment() {
        const APIOPTIONS: {
            [name: string]: string;
        } = {
            prefix: '/en/coralsports',
        };

        return this.vanillaApiService.get('CampaignData', {}, APIOPTIONS);
    }

    /**
     * calls OTF API to return user prediction status
     * @returns Observable<boolean>
     */
    public getOtfSegmentUserStatus() {
        this.getOtfUserStatusApi().subscribe((res) => {
            if (typeof res.status === 'boolean') {
                this.storageService.set(this.OTF_SEGMENT_STORE_KEYTEXT, { user: this.userService.username, segment: !res.status });
                this.isOTFAvailable.next(this.storageService.get(this.OTF_SEGMENT_STORE_KEYTEXT).segment);
                return this.storageService.get(this.OTF_SEGMENT_STORE_KEYTEXT).segment;
            } else {
                return false;
            }
        });
    }

    /**
     * calls predictionStatus API to return userStatus
     */
    private getOtfUserStatusApi() {
        const baseUrl = this.environment['ONE_TWO_FREE_API_ENDPOINT'];
        const APIOPTIONS = {
            headers: {
                ['token']: this.userService.bppToken,
            },
        } as any;
        return this.vanillaApiService.get(`${baseUrl}/api/v1/predictionStatus/${this.userService.username}/${this.getCustomerId()}`, {}, APIOPTIONS);
    }

    /**
     * Get CustomerId from clientconfig
     * @returns : string
     */
    private getCustomerId(): string {
        const clientConfig = (this.windowRef.nativeWindow && this.windowRef.nativeWindow.clientConfig) || {};
        let customerId = '';
        if (clientConfig && clientConfig.vnClaims) {
            customerId = clientConfig.vnClaims[this.NAME_IDENTIFIER_KEY];
        }
        return customerId;
    }
}
